import type {
  AbortTripInput,
  AssignmentSuggestion,
  AssignTripRequest,
  DriverBalance,
  DriverCaptureEntryView,
  DriverTopUpInput,
  EligibilityOverrideView,
  GrantEligibilityOverrideInput,
  HandoverTripInput,
  OfficeCaptureEventInput,
  OfficeCaptureMediaConfirmInput,
  OfficeCaptureMediaPresignInput,
  OfficeCaptureOutcome,
  Page,
  SyncMediaConfirmResponse,
  SyncMediaPresignResponse,
  TripCashReconciliationView,
  TripCashSettlementInput,
  TripCashSettlementView,
  TripHandoverView,
} from '@chukua/common';
import { api } from '../../shared/shared.api';
import { toListParams } from '../../shared/policies/list-params';
import type { EligibilityResponse } from './policies/picker-candidates.policy';
import type { SuggestionPickerQuery } from './policies/suggestion-picker-query';
import type { SuggestionWindow } from './policies/suggestion-window.policy';

export function listAssignmentSuggestions(tripId: string, query: SuggestionPickerQuery, window: SuggestionWindow): Promise<Page<AssignmentSuggestion>> {
  const params = toListParams(query);
  params.set('windowStart', window.start.toISOString());
  params.set('windowEnd', window.end.toISOString());
  return api<Page<AssignmentSuggestion>>(`/trips/${tripId}/assignment-suggestions?${params}`);
}

/** What the eligibility call needs of a trip: the job and the zones it is judged against. */
export interface TripRoute {
  jobId: string;
  originZoneCode: string | null;
  destinationZoneCode: string | null;
}

export function getTripRoute(tripId: string): Promise<TripRoute> {
  return api<TripRoute>(`/trips/${tripId}`);
}

/** Every truck, trailer and driver judged for the trip's job in the window, unpaged by design (`POST /dispatch/eligibility`). */
export function evaluateEligibility(
  route: { jobId: string; originZoneCode: string; destinationZoneCode: string },
  window: SuggestionWindow,
): Promise<EligibilityResponse> {
  const body = { ...route, windowStart: window.start.toISOString(), windowEnd: window.end.toISOString() };
  return api<EligibilityResponse>('/dispatch/eligibility', { method: 'POST', body: JSON.stringify(body) });
}

/** The window goes with the choice so the record of how the crew ranked is for the list the dispatcher was looking at. */
export async function assignCrew(tripId: string, crew: Pick<AssignTripRequest, 'truckId' | 'trailerId' | 'driverId'>, window: SuggestionWindow): Promise<void> {
  const body: AssignTripRequest = { ...crew, windowStart: window.start, windowEnd: window.end };
  await api<unknown>(`/trips/${tripId}/assignment`, { method: 'PUT', body: JSON.stringify(body) });
}

/** `GET /dispatch/fleet-availability`: units by truck status, and trailers that are on no truck (gitaumoses4/chukua#55). */
export interface FleetAvailability {
  units: Record<'available' | 'on_trip' | 'in_yard' | 'in_workshop' | 'off_road', number>;
  spareTrailers: { available: number; onTrip: number };
}

export function getFleetAvailability(): Promise<FleetAvailability> {
  return api<FleetAvailability>('/dispatch/fleet-availability');
}

/** One row of the board's trips panel (`GET /dispatch/board/trips`). */
export interface BoardTrip {
  id: string;
  number: string;
  status: string;
  jobId: string;
  jobNumber: string | null;
  customerName: string | null;
  truckId: string | null;
  truckRegistration: string | null;
  driverName: string | null;
  originZoneCode: string | null;
  destinationZoneCode: string | null;
  dispatchedAt: string | null;
}

export function listBoardTrips(query: { page: number; pageSize: number }): Promise<Page<BoardTrip>> {
  return api<Page<BoardTrip>>(`/dispatch/board/trips?${toListParams(query)}`);
}

export interface CrewMember {
  id: string;
  label: string;
}

export interface ReadinessItem {
  key: string;
  label: string;
  met: boolean;
  overridden: boolean;
}

/** A resource the engine refuses, with every check that blocks (`409 not_eligible`, and the preview's `eligibility.blocks`). */
export interface EligibilityBlock {
  resource: 'truck' | 'trailer' | 'driver' | 'combination';
  id: string | null;
  label: string;
  checks: EligibilityCheckView[];
}

export interface MoneyAmount {
  amountMinor: number;
  currency: 'KES';
}

export interface TripDispatchRecord {
  id: string;
  dispatchedAt: string;
  expectedStart: string | null;
  expectedEnd: string | null;
  fuelAllocationLitres: number | null;
  advance: MoneyAmount | null;
  expectedTolls: MoneyAmount | null;
  expectedWeighbridge: MoneyAmount | null;
}

/** Whether the plan, padded for rest, reaches one deadline; `no_route` means no verdict (the map gave no times), never a guess. */
export type DeadlineVerdict =
  { status: 'not_applicable' | 'no_deadline' | 'no_route' } | { status: 'on_time' | 'at_risk'; arrivalAt: string; deadlineAt: string; slackSeconds: number };

export interface PlanDeadlines {
  delivery: DeadlineVerdict;
  emptyReturn: DeadlineVerdict;
}

/** `GET /trips/:id/dispatch-preview`: what the dispatch screen shows before its button. */
export interface DispatchPreview {
  trip: {
    id: string;
    number: string;
    jobId: string;
    status: string;
    originZoneCode: string | null;
    destinationZoneCode: string | null;
    windowStart: string | null;
    windowEnd: string | null;
  };
  crew: { truck: CrewMember | null; trailer: CrewMember | null; driver: CrewMember | null };
  readiness: { ready: boolean; items: ReadinessItem[] };
  eligibility: { isEligible: boolean; blocks: EligibilityBlock[] };
  /** Null when the trip has no plan, or has already left. */
  plan: { id: string } | null;
  planDeadlines: PlanDeadlines | null;
  timeline: { start: string; end: string };
  dispatch: TripDispatchRecord | null;
  /** What the crew's driver still owes the company from earlier trips, per currency; empty when nothing, or when the viewer may not see the ledger. */
  driverOwes: DriverBalance[];
}

export interface DispatchAllocations {
  fuelAllocationLitres?: number;
  advance?: MoneyAmount;
  expectedTolls?: MoneyAmount;
  expectedWeighbridge?: MoneyAmount;
}

export function getDispatchPreview(tripId: string): Promise<DispatchPreview> {
  return api<DispatchPreview>(`/trips/${tripId}/dispatch-preview`);
}

/** The key makes a retry (a double click, a dropped connection) return the first dispatch instead of failing. */
export function dispatchTrip(tripId: string, idempotencyKey: string, allocations: DispatchAllocations): Promise<{ trip: { number: string } }> {
  return api<{ trip: { number: string } }>(`/trips/${tripId}/dispatch`, { method: 'POST', body: JSON.stringify({ idempotencyKey, ...allocations }) });
}

/** Stops a dispatched trip: the crew is freed, unspent cash is taken back and the job goes back to planning when it was its last open trip. */
export function abortTrip(tripId: string, input: AbortTripInput): Promise<{ number: string }> {
  return api<{ number: string }>(`/trips/${tripId}/abort`, { method: 'POST', body: JSON.stringify(input) });
}

export function listTripHandovers(tripId: string): Promise<TripHandoverView[]> {
  return api<TripHandoverView[]>(`/trips/${tripId}/handovers`);
}

/** The key makes a retry return the first handover rather than moving the trip twice. */
export function handOverTrip(tripId: string, input: HandoverTripInput): Promise<{ trip: { number: string }; handover: TripHandoverView }> {
  return api<{ trip: { number: string }; handover: TripHandoverView }>(`/trips/${tripId}/handover`, { method: 'POST', body: JSON.stringify(input) });
}

/** More cash for the driver on a trip already out; the key makes a double click one top-up. */
export async function topUpTrip(tripId: string, input: DriverTopUpInput): Promise<void> {
  await api<unknown>(`/trips/${tripId}/advance-top-ups`, { method: 'POST', body: JSON.stringify(input) });
}

/** What the phone, or the office for the driver, recorded on the trip (gitaumoses4/chukua#79). */
export function listDriverCapture(tripId: string): Promise<DriverCaptureEntryView[]> {
  return api<DriverCaptureEntryView[]>(`/trips/${tripId}/driver-capture`);
}

/** The driver is the trip's current one; the key makes a retry of the same step one event. */
export function recordDriverStep(tripId: string, event: OfficeCaptureEventInput): Promise<OfficeCaptureOutcome> {
  return api<OfficeCaptureOutcome>(`/trips/${tripId}/driver-capture/events`, { method: 'POST', body: JSON.stringify(event) });
}

/** The phone's three calls for a photo: ask for a URL, send the file to storage, confirm it. The file goes to storage, never through the API. */
export async function uploadDriverPhoto(tripId: string, file: File, clientMediaId: string): Promise<void> {
  const contentType = file.type as OfficeCaptureMediaPresignInput['contentType'];
  const target = await api<SyncMediaPresignResponse>(`/trips/${tripId}/driver-capture/media/presign`, {
    method: 'POST',
    body: JSON.stringify({ contentType, sizeBytes: file.size } satisfies OfficeCaptureMediaPresignInput),
  });
  const form = new FormData();
  for (const [name, value] of Object.entries(target.fields)) form.append(name, value);
  form.append('file', file);
  const stored = await fetch(target.url, { method: 'POST', body: form });
  if (!stored.ok) throw new Error(`Storage refused the photo (${stored.status})`);
  await api<SyncMediaConfirmResponse>(`/trips/${tripId}/driver-capture/media/confirm`, {
    method: 'POST',
    body: JSON.stringify({ objectKey: target.objectKey, clientMediaId } satisfies OfficeCaptureMediaConfirmInput),
  });
}

export function getTripCashReconciliation(tripId: string): Promise<TripCashReconciliationView> {
  return api<TripCashReconciliationView>(`/trips/${tripId}/cash-reconciliation`);
}

/** The key makes a retry return the first settlement instead of taking the cash twice. */
export function settleTripCash(tripId: string, input: TripCashSettlementInput): Promise<TripCashSettlementView> {
  return api<TripCashSettlementView>(`/trips/${tripId}/cash-settlements`, { method: 'POST', body: JSON.stringify(input) });
}

export type CheckStatus = 'pass' | 'warn' | 'block';

/** One check of an eligibility answer (`POST /dispatch/eligibility`), as the API sends it. `override` is present on a block an approver overrode for the trip asked about. */
export interface EligibilityCheckView {
  check: string;
  status: CheckStatus;
  code: string;
  reason: string | null;
  nonOverridable?: boolean;
  override?: { overrideId: string; reason: string; approver: { id: string; name: string } | null; grantedAt: string };
}

export interface OverrideRequester {
  id: string;
  name: string;
  email: string;
}

/** What the approver sends: the API reads the window as ISO instants. */
export type GrantOverrideBody = Omit<GrantEligibilityOverrideInput, 'windowStart' | 'windowEnd' | 'pickup'> & {
  windowStart: string;
  windowEnd: string;
  pickup?: { zoneCode: string; at: string };
};

export function grantEligibilityOverride(tripId: string, body: GrantOverrideBody): Promise<EligibilityOverrideView> {
  return api<EligibilityOverrideView>(`/trips/${tripId}/eligibility-override`, { method: 'POST', body: JSON.stringify(body) });
}

export function listTripEligibilityOverrides(tripId: string): Promise<EligibilityOverrideView[]> {
  return api<EligibilityOverrideView[]>(`/trips/${tripId}/eligibility-overrides`);
}

/** Active dispatch users the approver can name as the one who asked, never the approver. */
export function searchOverrideRequesters(q: string | undefined): Promise<OverrideRequester[]> {
  return api<OverrideRequester[]>(`/dispatch/override-requesters?q=${encodeURIComponent(q ?? '')}`);
}
